import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Award } from 'lucide-react';

/**
 * The full Badges catalog. Each badge has a `check(user)` predicate that
 * returns true when the user has earned it. Kept colocated with the
 * card so adding/removing badges is a one-file change.
 */
export const BADGES = [
  { id: 'first_steps', emoji: '🐣', nameEn: 'First Steps', nameEs: 'Primeros Pasos', descEn: 'View your first phrase', descEs: 'Ver tu primera frase', check: (u) => u.phrases_viewed > 0 },
  { id: 'explorer', emoji: '🔍', nameEn: 'Explorer', nameEs: 'Explorador', descEn: 'View 50 phrases', descEs: 'Ver 50 frases', check: (u) => (u.phrases_viewed || 0) >= 50 },
  { id: 'phrase_master', emoji: '📚', nameEn: 'Phrase Master', nameEs: 'Maestro', descEn: 'View 100 phrases', descEs: 'Ver 100 frases', check: (u) => (u.phrases_viewed || 0) >= 100 },
  { id: 'scholar', emoji: '🎓', nameEn: 'Scholar', nameEs: 'Erudito', descEn: 'View 500 phrases', descEs: 'Ver 500 frases', check: (u) => (u.phrases_viewed || 0) >= 500 },
  { id: 'streak_7', emoji: '🔥', nameEn: '7 Day Streak', nameEs: 'Racha 7 Días', descEn: 'Keep a 7-day streak', descEs: 'Mantén racha de 7 días', check: (u) => (u.longest_streak || 0) >= 7 },
  { id: 'streak_30', emoji: '⭐', nameEn: '30 Day Streak', nameEs: 'Racha 30 Días', descEn: 'Keep a 30-day streak', descEs: 'Mantén racha de 30 días', check: (u) => (u.longest_streak || 0) >= 30 },
  { id: 'streak_100', emoji: '💯', nameEn: '100 Day Streak', nameEs: 'Racha 100 Días', descEn: 'Keep a 100-day streak', descEs: 'Mantén racha de 100 días', check: (u) => (u.longest_streak || 0) >= 100 },
  { id: 'social', emoji: '🦋', nameEn: 'Social Butterfly', nameEs: 'Mariposa Social', descEn: 'Make 5 friends', descEs: 'Haz 5 amigos', check: (u) => (u.friends?.length || 0) >= 5 },
  { id: 'collector', emoji: '💎', nameEn: 'Collector', nameEs: 'Coleccionista', descEn: 'Save 10 favorites', descEs: 'Guardar 10 favoritas', check: (u) => (u.favorite_phrases?.length || 0) >= 10 },
  { id: 'vip', emoji: '👑', nameEn: 'VIP', nameEs: 'VIP', descEn: 'VIP Subscription', descEs: 'Suscripción VIP', check: (u) => u.subscription_tier && u.subscription_tier !== 'free' },
];

export default function AccountBadgesCard({ user, isSpanish = false }) {
  if (!user) return null;
  const earnedCount = BADGES.filter((b) => b.check(user)).length;
  return (
    <Card className="mb-6" data-testid="badges-card">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Award className="h-5 w-5 text-primary" />
          {isSpanish ? 'Insignias' : 'Badges'}
          <span className="text-sm text-muted-foreground font-normal">
            ({earnedCount}/{BADGES.length})
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-5 gap-2">
          {BADGES.map((badge) => {
            const earned = badge.check(user);
            return (
              <TooltipProvider key={badge.id}>
                <Tooltip delayDuration={200}>
                  <TooltipTrigger asChild>
                    <div
                      className={`text-center p-1.5 rounded-lg cursor-help transition-all ${
                        earned ? 'bg-primary/10' : 'bg-muted/50 opacity-40 grayscale'
                      }`}
                    >
                      <div className="text-xl">{badge.emoji}</div>
                      <p className="text-[9px] font-medium leading-tight mt-0.5">
                        {isSpanish ? badge.nameEs : badge.nameEn}
                      </p>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-[140px] text-center">
                    <p className="text-xs font-medium">
                      {isSpanish ? badge.nameEs : badge.nameEn}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {isSpanish ? badge.descEs : badge.descEn}
                    </p>
                    {earned && (
                      <p className="text-xs text-green-500 mt-0.5">Earned!</p>
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
